import { useState, type ReactNode } from 'react';
import { ApiError } from '../api/client.js';
import { useExplanation } from '../api/hooks.js';
import { Money } from './Money.js';

export function Panel({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="panel">
      <header className="panel__header">
        <div>
          <h2 className="panel__title">{title}</h2>
          {subtitle !== undefined && <p className="panel__subtitle">{subtitle}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * Errors carry their request id.
 *
 * Every response echoes `x-request-id`, and every audit row records it. Showing
 * it means a support conversation starts with an identifier that leads straight
 * to the log line, instead of "it said something went wrong".
 */
export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof ApiError ? error.message : 'Something went wrong.';
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <div className="alert" role="alert">
      {message}
      {requestId !== undefined && <span className="alert__id">Reference {requestId}</span>}
    </div>
  );
}

interface ActivityRow {
  id: string;
  occurredAt: string;
  eventType: string;
  accountType: string;
  amount: { amountCents: number; currency: 'USD' };
  conversionId?: string | null;
}

/**
 * Ledger activity.
 *
 * Still shows the raw event and account names rather than inventing friendlier
 * ones: this is the view people use to check the numbers, and being literal is
 * worth more than being pretty.
 *
 * What used to be missing was any way to find out what a line *meant*, because
 * the row carried no link to the purchase behind it. It does now, so each row
 * that has one offers "Why?" and gets a real explanation from the server rather
 * than a label this component made up.
 */
export function ActivityTable({
  rows,
  loading,
}: {
  rows: ActivityRow[] | undefined;
  loading: boolean;
}) {
  // One at a time. Several expanded rows turn a table people are using to
  // check figures into a wall of prose.
  const [expanded, setExpanded] = useState<string | null>(null);

  if (loading) return <p className="empty">Loading…</p>;
  if (rows === undefined || rows.length === 0) {
    return <p className="empty">Nothing yet.</p>;
  }

  return (
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th>When</th>
            <th>Event</th>
            <th>Account</th>
            <th className="table__right">Amount</th>
            <th>
              <span className="visually-hidden">Explanation</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const conversionId = row.conversionId ?? null;
            const isOpen = expanded === row.id;

            return [
              <tr key={row.id}>
                <td className="table__muted">
                  {new Date(row.occurredAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </td>
                <td>{row.eventType.replaceAll('_', ' ')}</td>
                <td className="table__muted">{row.accountType.replaceAll('_', ' ')}</td>
                <td className="table__right">
                  <Money value={row.amount} />
                </td>
                <td className="table__right">
                  {conversionId !== null && (
                    <button
                      className="button button--quiet button--small"
                      aria-expanded={isOpen}
                      onClick={() => setExpanded(isOpen ? null : row.id)}
                    >
                      {isOpen ? 'Hide' : 'Why?'}
                    </button>
                  )}
                </td>
              </tr>,
              isOpen && conversionId !== null ? (
                <tr key={`${row.id}-why`}>
                  <td colSpan={5} className="table__expanded">
                    <Explanation conversionId={conversionId} />
                  </td>
                </tr>
              ) : null,
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The server's sentence, not ours.
 *
 * `summary` is composed server-side for whichever party is asking, which is why
 * this component does no phrasing of its own — the same conversion reads
 * differently to a fan and to a creator, and deciding that here would mean the
 * client knowing which one it is talking to.
 */
function Explanation({ conversionId }: { conversionId: string }) {
  const explanation = useExplanation(conversionId);

  if (explanation.isPending) return <p className="empty">Looking it up…</p>;
  if (explanation.isError) return <ErrorNote error={explanation.error} />;

  const data = explanation.data;
  const rateCard = data.commission.rateCard;

  return (
    <div className="why">
      <p className="why__summary">{data.summary}</p>

      <dl className="why__facts">
        <div>
          <dt>Shop</dt>
          <dd>{data.purchase.merchantName}</dd>
        </div>
        <div>
          <dt>Purchase</dt>
          <dd>
            <Money value={data.purchase.amount} />
          </dd>
        </div>
        <div>
          <dt>Commission</dt>
          <dd>
            <Money value={data.commission.charged} />
            {rateCard !== null && (
              <span className="why__aside">
                {' '}
                {rateCard.matchesCharged
                  ? `(${(rateCard.commissionBps / 100).toFixed(2)}%)`
                  : /* The rate card and what was actually charged disagree.
                       Shown rather than hidden: the difference is the signal,
                       and hiding it would make this the one place someone
                       cannot find out. */
                    `(rate card says ${(rateCard.commissionBps / 100).toFixed(2)}%, which would have been $${(
                      rateCard.wouldHaveBeen.amountCents / 100
                    ).toFixed(2)})`}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Tracked by</dt>
          <dd>
            {data.purchase.provider} · {data.purchase.source.replaceAll('_', ' ')}
          </dd>
        </div>
      </dl>

      <p className="why__detail">{data.attribution.explanation}</p>
    </div>
  );
}
